import { useState, useEffect } from "react";
import { Skeleton } from "./ui/skeleton";
import { SKELETON_DELAY } from "../utilities/useDelayedLoading";

interface LoadingIndicatorProps {
  variant?: "page" | "inline";
  delay?: boolean;
}

function LoadingIndicator({ variant = "page", delay = false }: LoadingIndicatorProps) {
  const [visible, setVisible] = useState(!delay);

  useEffect(() => {
    if (!delay) return;
    const timer = setTimeout(() => setVisible(true), SKELETON_DELAY);
    return () => clearTimeout(timer);
  }, [delay]);

  if (!visible) return null;

  if (variant === "inline") {
    return (
      <div className={`w-full space-y-3 p-4${delay ? " animate-in fade-in-0" : ""}`}>
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    );
  }

  return (
    <div className={`flex h-full w-full${delay ? " animate-in fade-in-0" : ""}`}>
      {/* Sidebar skeleton */}
      <div className="hidden md:flex w-[256px] shrink-0 flex-col gap-4 border-r border-border/40 bg-background p-4">
        <Skeleton className="h-10 w-full rounded-lg" />
        <div className="space-y-2 mt-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full rounded-md" />
          ))}
        </div>
      </div>
      {/* Content area skeleton */}
      <div className="flex-1 p-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-5/6" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
        <div className="space-y-3 mt-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default LoadingIndicator;
